'use strict'

/**
 * ブラウザのリサイズを最適化する
 *
 * 機能
 * 1. 'debounceResize' - リサイズ中に複数回起こるリサイズを最適化して最後のリサイズ時にイベントを発火
 * 2. 'debounceResizeViewport' - Mobile Safariなどのリサイズを最適化
 * ===========================================
 * @TODO destroy method
 *
 * @TEST
 * import OptimizeResize from '@/js/helpers/optimize-resize-event'
 * import '@/js/helpers/optimize-scroll-event'
 *
 * const optimizeResize = new OptimizeResize()
 *
 * document.getElementById('log').textContent = optimizeResize.getViewportHeight()
 * document.getElementById('log2').textContent = window.innerHeight
 *
 * window.addEventListener('resize', () => {
 *   document.getElementById('log').textContent = optimizeResize.getViewportHeight()
 *   document.getElementById('log2').textContent = window.innerHeight
 * })
 * window.addEventListener('debounceResizeViewport', () => {
 *   count++
 *   document.getElementById('log3').textContent = count
 * })
 */

import { debounce } from 'debounce'

const fireEvent = (name, obj = window) => {
  if (document.createEventObject) {
    obj.fireEvent(name)
  } else {
    const evt = document.createEvent('HTMLEvents')
    evt.initEvent(name, false, true)
    obj.dispatchEvent(evt)
  }
}

const throttle = (type, name, obj = window) => {
  let running = false
  const func = () => {
    if (running) {
      return
    }
    running = true
    window.requestAnimationFrame(() => {
      fireEvent(name, obj)
      running = false
    })
  }
  obj.addEventListener(type, func)
}

export default class optimizeResize {
  constructor() {
    this.init()
  }

  getViewportHeight() {
    if (!navigator.userAgent.match(/iphone|ipod|ipad|android/i)) {
      return window.innerHeight
    }

    // Store initial orientation
    this.axis = Math.abs(window.orientation)

    // And hoist cached dimensions
    this.dims = { w: 0, h: 0 }

    /**
     * Creates an element with a height of 100vh since iOS accurately
     * reports vp height (but not window.innerHeight). Then destroy it.
     */
    const createRuler = () => {
      let ruler = document.createElement('div')

      ruler.style.position = 'fixed'
      ruler.style.height = '100vh'
      ruler.style.width = 0
      ruler.style.top = 0

      document.documentElement.appendChild(ruler)

      // Set cache conscientious of device orientation
      this.dims.w = (this.axis === 90) ? ruler.offsetHeight : window.innerWidth
      this.dims.h = (this.axis === 90) ? window.innerWidth : ruler.offsetHeight

      // Clean up after ourselves
      document.documentElement.removeChild(ruler)
      ruler = null
    }

    createRuler()

    /**
     * Returns window's cached innerHeight measurement
     * based on viewport height and device orientation
     * @return {number} - window's innerHeight measurement in pixels
     */
    return (Math.abs(window.orientation) !== 90) ? this.dims.h : this.dims.w
  }

  debounceResize() {
    fireEvent('debounceResize')

    const width = document.body.clientWidth || window.innerWidth
    const height = this.getViewportHeight()
    const orientation = window.orientation || 0
    if (this.lastWidth !== width || this.lastHeight !== height || this.lastOrientation !== orientation) {
      this.lastWidth = width
      this.lastHeight = height
      this.lastOrientation = orientation
      fireEvent('debounceResizeViewport')

      this.setFirstviewCSSCustomProperty()
      this.setViewportCSSCustomProperty()
    }
  }

  debounceScroll() {
    const y = window.scrollY || window.pageYOffset

    if (y < 0) {
      return
    }

    if (y === 0) {
      this.setFirstviewCSSCustomProperty()
      this.setViewportCSSCustomProperty()
    }
  }

  setViewportCSSCustomProperty() {
    const vh = this.lastHeight * 0.01
    document.documentElement.style.setProperty('--viewport-vh', `${vh}px`)
  }

  setFirstviewCSSCustomProperty() {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--firstview-vh', `${vh}px`)
  }

  init() {
    throttle('resize', 'optimizedResize')

    this.lastWidth = document.body.clientWidth || window.innerWidth
    this.lastHeight = this.getViewportHeight()
    this.lastOrientation = window.orientation || 0

    this.setFirstviewCSSCustomProperty()
    this.setViewportCSSCustomProperty()

    window.addEventListener('optimizedResize', debounce(this.debounceResize.bind(this), 500))
    window.addEventListener('optimizedScroll', debounce(this.debounceScroll.bind(this), 500))
  }

  destroy() {
    window.removeEventListener('optimizedResize', debounce(this.debounceResize.bind(this), 500))
    window.removeEventListener('optimizedScroll', debounce(this.debounceScroll.bind(this), 500))
  }
}
